import React, { useMemo, useState } from "react";
import { Table, Button } from "antd";
import {
  PlusOutlined,
  ExpandAltOutlined,
  ShrinkOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { MachineItem } from "shared/api";
import { useTaskHubState } from "shared/hooks/useTaskHubState";
import { useTaskHubFilters } from "shared/hooks/useTaskHubFilters";
import { useTaskHubTree } from "shared/hooks/useTaskHubTree";
import { useTaskHubTreeColumns } from "shared/hooks/useTaskHubTreeColumns";
import { AddTaskModal } from "./AddTaskModal";
import { EditTaskModal } from "./EditTaskModal";
import { SectionModal } from "./SectionModal";
import { getThemeColor } from "./taskHubHelpers";
import { collectSectionKeys } from "./taskHubTreeUtils";
import { UserSelectOption, SelectOption } from "./types";

interface TaskHubTreeProps {
  transformedUsers: UserSelectOption[];
  machinesData: MachineItem[] | undefined;
  suppliersCompaniesFormatted: SelectOption[] | undefined;
  modalState: ReturnType<typeof useTaskHubState>;
  filters: ReturnType<typeof useTaskHubFilters>;
  refetchExecutions: () => void;
}

export const TaskHubTree: React.FC<TaskHubTreeProps> = ({
  transformedUsers,
  machinesData,
  suppliersCompaniesFormatted,
  modalState,
  filters,
  refetchExecutions,
}) => {
  const { t } = useTranslation();
  const themeColor = getThemeColor();

  const sectionParams = {
    expand: "users,product_cards,companies",
    name__icontains: filters.debouncedTaskNameValue || undefined,
    periodicity: filters.debouncedPeriodicityValue || undefined,
    priority__gte: filters.priorityFilters.minValue ?? undefined,
    priority__lte: filters.priorityFilters.maxValue ?? undefined,
    enabled: filters.enabledFilter,
    start_periodicity__gte: filters.startDateFilters.dateGreater ?? undefined,
    start_periodicity__lte: filters.startDateFilters.dateLesser ?? undefined,
    users__user__in: filters.userIds.length > 0 ? filters.userIds : undefined,
    trigger__in:
      filters.triggerInFilter.length > 0 ? filters.triggerInFilter : undefined,
  };

  const {
    contextHolder,
    rows,
    flatSections,
    isLoading,
    refetch,
    addTaskParent,
    closeAddTask,
    getDisallowedSectionIds,
    handlers,
    section,
  } = useTaskHubTree(modalState, sectionParams);

  const columns = useTaskHubTreeColumns({
    flatSections,
    getDisallowedSectionIds,
    filters,
    transformedUsers,
    ...handlers,
  });

  // Keep the tree fully expanded (so filtered matches stay visible) while still
  // letting the user collapse individual sections.
  const allSectionKeys = useMemo(() => collectSectionKeys(rows), [rows]);
  const [collapsedKeys, setCollapsedKeys] = useState<string[]>([]);
  const expandedRowKeys = allSectionKeys.filter(
    (key) => !collapsedKeys.includes(key)
  );

  const allCollapsed =
    allSectionKeys.length > 0 && expandedRowKeys.length === 0;

  const toggleExpandAll = () =>
    setCollapsedKeys(allCollapsed ? [] : allSectionKeys);

  return (
    <>
      {contextHolder}

      <div className="flex items-center gap-2 mb-2">
        <Button
          type="primary"
          style={{ backgroundColor: themeColor }}
          onClick={() => section.openCreate(null)}
        >
          {t("taskHub.sections.addSection") || "Add Section"}
          <PlusOutlined />
        </Button>

        <Button
          className="!inline-flex !items-center"
          icon={allCollapsed ? <ExpandAltOutlined /> : <ShrinkOutlined />}
          onClick={toggleExpandAll}
          disabled={allSectionKeys.length === 0}
        >
          {allCollapsed
            ? t("taskHub.sections.expandAll") || "Expand all"
            : t("taskHub.sections.collapseAll") || "Collapse all"}
        </Button>
      </div>

      <Table
        className="thub-table"
        dataSource={rows}
        columns={columns}
        loading={isLoading}
        rowKey="key"
        pagination={false}
        scroll={{ x: true }}
        rowClassName={(record) =>
          record.rowType === "section" ? "thub-section-row" : ""
        }
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) =>
            setCollapsedKeys((prev) =>
              expanded
                ? prev.filter((key) => key !== record.key)
                : [...prev, record.key]
            ),
        }}
      />

      <AddTaskModal
        suppliersCompaniesFormatted={suppliersCompaniesFormatted}
        machinesData={machinesData}
        transformedUsers={transformedUsers}
        open={modalState.isAddModalOpen}
        parentSectionId={addTaskParent}
        onClose={closeAddTask}
        refetchExecutions={refetchExecutions}
      />

      <EditTaskModal
        suppliersCompaniesFormatted={suppliersCompaniesFormatted}
        transformedUsers={transformedUsers}
        open={modalState.isEditModalOpen}
        taskId={modalState.editingTask}
        onClose={() => modalState.setIsEditModalOpen(false)}
        refetch={refetch}
        refetchExecutions={refetchExecutions}
      />

      <SectionModal
        open={section.open}
        mode={section.mode}
        sectionOptions={section.options}
        loading={section.loading}
        initialValues={section.initialValues}
        onClose={section.close}
        onSubmit={section.submit}
      />
    </>
  );
};
